import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  keyframes,
} from '@mui/material';
import { GppGoodOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// 青藍色（依品牌 icon 取色）
const CYAN = '#5BB8D4';
const CYAN_DARK = '#3FA0C2';

// 資安動畫：盾牌呼吸 + 向外擴散的防護波紋
const shieldPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
`;
const ripple = keyframes`
  0% { transform: scale(0.6); opacity: 0.55; }
  100% { transform: scale(2.2); opacity: 0; }
`;

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export default function PrivacyDisclaimerDialog({ open, onClose, onConfirm }: Props) {
  const { t } = useTranslation('ai-publish');
  const points = t('disclaimer.points', { returnObjects: true }) as string[];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 'calc(100vw - 32px)',
          maxWidth: 450,
          m: 0,
          borderRadius: '20px',
          overflow: 'hidden',
          bgcolor: '#fff', // 固定白底（不隨深色模式變深）
        },
      }}
    >
      {/* 上方藍底：動畫 icon（左）+ 標題（右），並排置中、白字 */}
      <Box
        sx={{
          height: 76,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${CYAN} 0%, ${CYAN_DARK} 100%)`,
          color: 'common.white',
        }}
      >
        {/* 資安動畫：盾牌呼吸 + 防護波紋（波紋以 icon 為中心） */}
        <Box
          sx={{
            position: 'relative',
            width: 52,
            height: 52,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {[0, 1.1].map((delay) => (
            <Box
              key={delay}
              sx={{
                position: 'absolute',
                width: 48,
                height: 48,
                borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.6)',
                animation: `${ripple} 2.2s ease-out ${delay}s infinite`,
              }}
            />
          ))}
          <GppGoodOutlined
            sx={{
              fontSize: 40,
              position: 'relative',
              animation: `${shieldPulse} 2.2s ease-in-out infinite`,
            }}
          />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'common.white' }}>
          {t('disclaimer.title')}
        </Typography>
      </Box>

      {/* 下方白底內容 */}
      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="body2" sx={{ mb: 2, color: 'rgba(0,0,0,0.6)' }}>
          {t('disclaimer.intro')}
        </Typography>
        <Box
          component="ol"
          sx={{ pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}
        >
          {points.map((point) => (
            <Typography
              key={point}
              component="li"
              variant="body2"
              sx={{ lineHeight: 1.6, color: '#1a1a1a' }}
            >
              {point}
            </Typography>
          ))}
        </Box>
        <Alert
          severity="warning"
          variant="standard"
          sx={{
            mt: 2,
            borderRadius: '10px',
            bgcolor: '#FFF4E5',
            color: '#663C00',
            '& .MuiAlert-icon': { color: '#ED6C02' },
          }}
        >
          {t('disclaimer.warning')}
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: 'none', borderRadius: '33px', px: 2, color: CYAN_DARK }}
        >
          {t('disclaimer.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          sx={{
            textTransform: 'none',
            borderRadius: '33px',
            px: 3,
            fontWeight: 700,
            bgcolor: CYAN,
            '&:hover': { bgcolor: CYAN_DARK },
          }}
        >
          {t('disclaimer.accept')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
